/*
  SetBreakdownEditor — small inline editor for the optional
  `set_groups` list on an exercise. Lets a practitioner prescribe a
  progressive-loading sequence (e.g. "2×5 @ 10kg, then 3×5 @ 12.5kg")
  without duplicating the exercise row.

  Data shape (mirrored on the backend PDF renderers):
    ex.set_groups: [{ sets: str, reps: str, load: str }, ...]

  When `set_groups` is empty (or absent) the flat sets/reps/load
  inputs above continue to drive the display. When populated, they
  take priority both on-screen and in the printed PDF.

  This component intentionally stays "silent" — collapsed to a
  single "+ Add set group" button — for exercises that don't need
  multi-weight prescription, so simple exercises look the same as
  they did before.
*/
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";

export default function SetBreakdownEditor({ ex, locked, onChange }) {
  const groups = Array.isArray(ex?.set_groups) ? ex.set_groups : [];

  function patchGroups(next) {
    onChange({ set_groups: next });
  }
  function addGroup() {
    // Start every new group empty. The rendered worksheet now prints
    // the flat sets/reps/load row FIRST (when populated) and each
    // set-group as an additional row underneath, so seeding the
    // first group from the flat fields would just print a duplicate.
    const seed = groups.length === 0
      ? { sets: "", reps: "", load: "" }
      : { sets: "", reps: groups[groups.length - 1]?.reps || "", load: "" };
    patchGroups([...groups, seed]);
  }
  function patchGroupAt(i, patch) {
    patchGroups(groups.map((g, gi) => (gi === i ? { ...g, ...patch } : g)));
  }
  function removeAt(i) {
    patchGroups(groups.filter((_, gi) => gi !== i));
  }

  if (groups.length === 0) {
    return (
      <button
        type="button"
        onClick={addGroup}
        disabled={locked}
        className="mt-1.5 text-2xs uppercase tracking-wide text-zinc-500 hover:text-indigo-700 disabled:opacity-50 disabled:hover:text-zinc-500 inline-flex items-center gap-1"
        data-testid={`ep-set-breakdown-add-${ex?.id || ""}`}
        title="Prescribe different weights across sets (e.g. 2×5 @ 10kg / 3×5 @ 12.5kg)"
      >
        <Plus className="h-2.5 w-2.5" /> Add set-group (multi-weight)
      </button>
    );
  }
  return (
    <div className="mt-1.5 border border-dashed border-indigo-200 rounded p-1.5 bg-indigo-50/30"
         data-testid={`ep-set-breakdown-${ex?.id || ""}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-2xs uppercase tracking-wide text-indigo-700 font-semibold">
          Set breakdown (additional set-groups)
        </div>
        <Button
          size="sm" variant="ghost"
          onClick={addGroup}
          disabled={locked}
          className="h-6 text-2xs"
          data-testid={`ep-set-breakdown-append-${ex?.id || ""}`}
        >
          <Plus className="h-2.5 w-2.5 mr-0.5" /> Group
        </Button>
      </div>
      <div className="space-y-1">
        {groups.map((g, i) => (
          <div key={i} className="grid grid-cols-[1.2fr_1.2fr_2fr_auto] gap-1 items-center">
            <Input
              value={g.sets || ""}
              onChange={(e) => patchGroupAt(i, { sets: e.target.value })}
              disabled={locked}
              placeholder="Sets"
              className="h-6 text-2xs px-1.5"
              data-testid={`ep-set-breakdown-sets-${i}`}
            />
            <Input
              value={g.reps || ""}
              onChange={(e) => patchGroupAt(i, { reps: e.target.value })}
              disabled={locked}
              placeholder="Reps"
              className="h-6 text-2xs px-1.5"
              data-testid={`ep-set-breakdown-reps-${i}`}
            />
            <Input
              value={g.load || ""}
              onChange={(e) => patchGroupAt(i, { load: e.target.value })}
              disabled={locked}
              placeholder="Load (e.g. 10 kg)"
              className="h-6 text-2xs px-1.5"
              data-testid={`ep-set-breakdown-load-${i}`}
            />
            <button
              onClick={() => removeAt(i)}
              disabled={locked}
              className="p-1 rounded hover:bg-red-50 text-red-600 disabled:opacity-40"
              title="Remove this set group"
              data-testid={`ep-set-breakdown-del-${i}`}
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
